import FirstLineContainer from "components/common/FirstLineContainer";
import React, { useEffect, useMemo, useState } from "react";
import RuleTable from "./RuleTable";
import { setLoading, setDialog } from "slices/appSlice";
import Api from "api";
import {
  useFetchRuleSet,
  useFetchRuleFields,
} from "hooks/useRuleConfigProcess";
import { useParams } from "react-router-dom";
import { Stack, Box, TextField, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoadingBackdrop from "components/LoadingBackDrop";
import { formErrorStrfromObj } from "utils/shared";

function EditRuleSet() {
  const { ruleSetId } = useParams();
  const dispatch = useDispatch();
  const [ruleSet, fetchRuleSet, isLoading] = useFetchRuleSet();
  const [priority, setPriority] = useState();
  const [ruleColumnMetaMap, setRuleColumnMetaMap] = useState(new Map());

  const [fieldList] = useFetchRuleFields();
  const navigate = useNavigate();

  useEffect(() => {
    if (ruleSet) setPriority(ruleSet.priority);
  }, [ruleSet]);

  const inputFieldKeys = useMemo(() => {
    if (!ruleSet?.ruleList?.length) return [];
    const allKeys = ruleSet.ruleList.flatMap(
      (rule) =>
        rule.ruleColumns
          ?.filter((c) => c.columnType === "INPUT")
          .map((c) => c.ruleFieldKey) || []
    );

    return Array.from(new Set(allKeys));
  }, [ruleSet]);

  const outputFieldKeys = useMemo(() => {
    if (!ruleSet?.ruleList?.length) return [];
    const allKeys = ruleSet.ruleList.flatMap(
      (rule) =>
        rule.ruleColumns
          ?.filter((c) => c.columnType === "OUTPUT")
          .map((c) => c.ruleFieldKey) || []
    );

    return Array.from(new Set(allKeys));
  }, [ruleSet]);

  const inputSelected = useMemo(() => {
    return fieldList.filter((f) =>
      inputFieldKeys.includes(f.ruleConfigFieldKey)
    );
  }, [fieldList, inputFieldKeys]);

  const outputSelected = useMemo(() => {
    return fieldList.filter((f) =>
      outputFieldKeys.includes(f.ruleConfigFieldKey)
    );
  }, [fieldList, outputFieldKeys]);

  useEffect(() => {
    fetchRuleSet(ruleSetId);
  }, [ruleSetId]);

  const rules = useMemo(() => {
    const ruleList = [];
    const metaMap = new Map();

    ruleSet?.ruleList?.forEach((r, i) => {
      const rule = {
        id: i,
        ruleKey: r.ruleConfigRuleKey,
        name: r.ruleConfigRuleName,
        priority: r.priority,
      };

      r.ruleColumns?.forEach((rc) => {
        const { columnType, ruleFieldKey, fieldValue, ruleConfigRuleInputKey } =
          rc;
        const key = `${r.ruleConfigRuleKey}_${columnType}_${ruleFieldKey}`;

        // Attach value to flattened row
        rule[`${columnType.toLowerCase()}_${ruleFieldKey}`] = fieldValue;

        // Store metadata
        metaMap.set(key, rc); // you can store only needed props if desired
      });

      ruleList.push(rule);
    });

    setRuleColumnMetaMap(metaMap);
    return ruleList;
  }, [ruleSet]);

  const onSubmit = async (rulesList, deletedRulesKey) => {
    dispatch(setLoading(true));

    try {
      const response = await Api.ruleConfigProcess.editRuleSet(
        ruleSetId,
        priority,
        rulesList,
        deletedRulesKey
      );

      if (response.status === 200) {
        dispatch(
          setDialog({
            message: "RuleSet saved successfully",
            title: "Success",
          })
        );
      }
      navigate(-1);
    } catch (error) {
      dispatch(
        setDialog({
          message: formErrorStrfromObj(error),
          title: error.message,
        })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  if (isLoading) return <LoadingBackdrop message="Loading ruleset" />;

  if (!isLoading && !ruleSet) return "Error fetching ruleset";

  return (
    <Box sx={{ height: "90vh", display: "flex", flexDirection: "column" }}>
      <FirstLineContainer>
        <Typography variant="h6" sx={{ textAlign: "left", flex: 1 }}>
          <Box component="span" fontWeight="fontWeightBold">
            Edit Ruleset
          </Box>
        </Typography>
      </FirstLineContainer>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Stack margin={1} spacing={1} direction={"row"} alignItems="center">
          <TextField
            value={ruleSet?.ruleConfigRuleSetName || ""}
            label="RuleSet Name"
            disabled
            sx={{ m: 1 }}
          />
          <TextField
            value={ruleSet?.ruleConfigProcessKey || ""}
            label="Process Key"
            disabled
            sx={{ m: 1 }}
          />
          <TextField
            value={priority || ""}
            label="Priority"
            onChange={(e) => {
              if (!isNaN(e.target.value)) setPriority(e.target.value);
            }}
            sx={{ m: 1 }}
          />
        </Stack>
      </Box>
      <RuleTable
        inputs={inputSelected}
        outputs={outputSelected}
        onSubmit={onSubmit}
        rules={rules}
        ruleColumnMetaMap={ruleColumnMetaMap}
        editScreen
        ruleSetKey={ruleSetId}
        fetchRuleSet={fetchRuleSet}
      />
    </Box>
  );
}

export default EditRuleSet;
